"use client";

/**
 * Chart timezone resolver — the single, mandatory entry point for every
 * widget, axis formatter, query-filter label, and Download Hub export.
 *
 * Why this exists:
 *   - Market-data charts must bucket time in a *stable* zone, not the
 *     viewer's browser. A "Monday spike" in São Paulo time is a
 *     different bucket in New York time, and the same chart must look
 *     identical to every viewer of a given dataset.
 *   - When (not if) the platform ships GCC and Europe data packages,
 *     the trading week shifts. Bucketing in viewer-time hides Sunday
 *     activity for European viewers of GCC data.
 *
 * What this enforces:
 *   - The returned `timezone` is a `ChartTimezone` brand — only this
 *     hook can mint one. `formatChartTimestamp` requires the brand,
 *     so passing `BROWSER_TZ` (or any unbranded string) is a compile
 *     error. The structural guarantee survives future refactors.
 *   - The mode (data / user / company) lives in component-local state.
 *     The default is "data" — viewers see the dataset's native zone and
 *     can override per chart. A future `<ChartTimezoneToggle>` simply
 *     calls the returned `setMode`.
 *
 * Future hook-up points (deliberately not built now):
 *   - User-level default ("always show charts in my time"): when the
 *     `users.chart_display_mode` column lands, read it from
 *     `useTimezones()` and seed `initialMode` from there.
 *   - Per-region override (`dataset.timezone` from the API): if a
 *     specific dataset declares its own zone, prefer it over
 *     `region.primaryTimezone` inside the resolver below.
 */

import { useCallback, useMemo, useState } from "react";

import { type ChartTimezone } from "./format";
import { useTimezones } from "./provider";
import { getRegion, type DataRegion, type RegionId } from "./regions";

/**
 * Display mode for chart timestamps.
 *
 *   - "data"    : the region's native timezone (default — stable across viewers)
 *   - "user"    : the viewer's resolved primary timezone
 *   - "company" : the viewer's company timezone (falls back to "data" for GAs)
 */
export type ChartTimezoneMode = "data" | "user" | "company";

export interface ChartTimezoneState {
  /** Branded TZ for formatChartTimestamp(). Re-derived when `mode` changes. */
  readonly timezone: ChartTimezone;
  /** Active display mode for this chart. */
  readonly mode: ChartTimezoneMode;
  /**
   * Switch the active mode. If "company" is requested while no company
   * is in the current context, falls back to "data" — the toggle UI
   * should already be hiding the option in that case.
   */
  readonly setMode: (next: ChartTimezoneMode) => void;
  /** Region metadata — pass to tooltip / axis label components. */
  readonly region: DataRegion;
  /** True when the user has a company TZ available (toggle visibility hint). */
  readonly companyAvailable: boolean;
  /** True when "user" mode resolves to a TZ different from the region's. */
  readonly userDistinct: boolean;
  /** True when "company" mode resolves to a TZ different from the region's. */
  readonly companyDistinct: boolean;
}

const DEFAULT_MODE: ChartTimezoneMode = "data";

/**
 * Resolve and track the active timezone for a chart bound to `regionId`.
 *
 * @param regionId     The region the data belongs to.
 * @param initialMode  Starting mode. Defaults to "data" (stable across
 *                     viewers). When the user-level preference column
 *                     lands, callers can pass that instead.
 */
export function useChartTimezone(
  regionId: RegionId,
  initialMode: ChartTimezoneMode = DEFAULT_MODE,
): ChartTimezoneState {
  const region = getRegion(regionId);
  const tz = useTimezones();
  const [mode, setModeState] = useState<ChartTimezoneMode>(initialMode);

  const timezone = useMemo<ChartTimezone>(() => {
    let raw: string;
    switch (mode) {
      case "user":
        raw = tz.primary;
        break;
      case "company":
        raw = tz.company ?? region.primaryTimezone;
        break;
      case "data":
      default:
        raw = region.primaryTimezone;
        break;
    }
    // The cast is the *only* place a ChartTimezone is minted in the codebase.
    // Anywhere else relies on this hook returning the brand.
    return raw as ChartTimezone;
  }, [mode, tz.primary, tz.company, region.primaryTimezone]);

  const companyAvailable = tz.company !== null;
  const userDistinct = tz.primary !== region.primaryTimezone;
  const companyDistinct =
    tz.company !== null && tz.company !== region.primaryTimezone;

  const setMode = useCallback(
    (next: ChartTimezoneMode) => {
      if (next === "company" && !companyAvailable) {
        setModeState("data");
        return;
      }
      setModeState(next);
    },
    [companyAvailable],
  );

  return {
    timezone,
    mode,
    setMode,
    region,
    companyAvailable,
    userDistinct,
    companyDistinct,
  };
}
